import { doc, getDoc, runTransaction, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

const CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
const CODE_LENGTH = 8;
const PENDING_REF_KEY = 'pendingReferralCode';

const generateCode = (): string => {
  let code = '';
  for (let i = 0; i < CODE_LENGTH; i++) {
    code += CODE_ALPHABET[Math.floor(Math.random() * CODE_ALPHABET.length)];
  }
  return code;
};

export const getOrCreateReferralCode = async (uid: string): Promise<string> => {
  const userRef = doc(db, 'users', uid);
  const snap = await getDoc(userRef);
  const existing = snap.data()?.referralCode;
  if (typeof existing === 'string' && existing.length > 0) return existing;

  for (let attempt = 0; attempt < 5; attempt++) {
    const candidate = generateCode();
    const codeRef = doc(db, 'referralCodes', candidate);
    const created = await runTransaction(db, async (tx) => {
      const codeSnap = await tx.get(codeRef);
      if (codeSnap.exists()) return false;
      tx.set(codeRef, { uid, createdAt: serverTimestamp() });
      tx.update(userRef, { referralCode: candidate });
      return true;
    });
    if (created) return candidate;
  }
  throw new Error('Could not generate a unique referral code. Please try again.');
};

export type ClaimResult = { ok: true; code: string } | { ok: false; reason: string };

export const claimCustomReferralCode = async (
  uid: string,
  requestedCode: string
): Promise<ClaimResult> => {
  const code = requestedCode.trim().toUpperCase();
  if (!/^[A-Z0-9]{3,20}$/.test(code)) {
    return { ok: false, reason: 'Use 3–20 letters or numbers, no spaces or symbols.' };
  }

  const userRef = doc(db, 'users', uid);
  const codeRef = doc(db, 'referralCodes', code);

  return runTransaction<ClaimResult>(db, async (tx) => {
    const codeSnap = await tx.get(codeRef);
    if (codeSnap.exists()) {
      if (codeSnap.data()?.uid !== uid) {
        return { ok: false, reason: 'That code is already taken. Try another one.' };
      }
      tx.update(userRef, { referralCode: code });
      return { ok: true, code };
    }
    tx.set(codeRef, { uid, createdAt: serverTimestamp() });
    tx.update(userRef, { referralCode: code });
    return { ok: true, code };
  });
};

export const resolveReferralCode = async (code: string): Promise<string | null> => {
  const trimmed = code.trim().toUpperCase();
  if (!trimmed) return null;
  const codeSnap = await getDoc(doc(db, 'referralCodes', trimmed));
  if (!codeSnap.exists()) return null;
  const data = codeSnap.data();
  return typeof data?.uid === 'string' ? data.uid : null;
};

export const buildReferralLink = (code: string): string =>
  `${window.location.origin}/register?ref=${encodeURIComponent(code)}`;

export const setPendingReferralCode = (code: string): void => {
  try {
    sessionStorage.setItem(PENDING_REF_KEY, code.trim().toUpperCase());
  } catch {
    // sessionStorage unavailable (private mode); silently skip
  }
};

export const getPendingReferralCode = (): string | null => {
  try {
    return sessionStorage.getItem(PENDING_REF_KEY);
  } catch {
    return null;
  }
};

export const clearPendingReferralCode = (): void => {
  try {
    sessionStorage.removeItem(PENDING_REF_KEY);
  } catch {
    // ignore
  }
};
